import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Transform snake_case keys to camelCase recursively
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, ch) => ch.toUpperCase());
}

function transformKeys(obj: any): any {
  if (Array.isArray(obj)) return obj.map(transformKeys);
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof File) && !(obj instanceof Blob)) {
    return Object.keys(obj).reduce((acc: any, key) => {
      acc[snakeToCamel(key)] = transformKeys(obj[key]);
      return acc;
    }, {});
  }
  return obj;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      timeout: 30000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add tenant context if available
        const tenant = Cookies.get('tenant');
        if (tenant) {
          config.headers['X-Tenant'] = tenant;
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // Transform keys to camelCase
        if (response.data && typeof response.data === 'object') {
          response.data = transformKeys(response.data);
        }
        return response;
      },
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Sanctum token expired or invalid — clear stored auth.
          // Don't hard-redirect; Zustand state clearing will cause
          // ProtectedRoute to redirect via React Router (no full page reload).
          this.clearTokens();
          localStorage.removeItem('auth-storage');
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth helpers
  public setTokens(accessToken: string, refreshToken?: string): void {
    Cookies.set('access_token', accessToken, {
      expires: 1, // 1 day
      secure: window.location.protocol === 'https:',
      sameSite: 'strict',
    });

    if (refreshToken) {
      Cookies.set('refresh_token', refreshToken, {
        expires: 30,
        secure: window.location.protocol === 'https:',
        sameSite: 'strict',
      });
    }
  }

  public getAccessToken(): string | undefined {
    return Cookies.get('access_token');
  }

  public clearTokens(): void {
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    Cookies.remove('tenant');
  }

  public isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!token;
  }

  // HTTP methods
  public async get<T>(url: string, params?: Record<string, any>, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get(url, { ...config, params });
    return response.data;
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete(url);
    return response.data;
  }

  public async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }
}

export const api = new ApiService();
export default api;
